import React, { useState, useEffect, Fragment } from "react";
import { Row, Col, Container, Form, FormGroup, Label, Input, Button, Card, CardBody } from "reactstrap";
import { Trash2, Edit2 } from "react-feather";
import axios from "axios";
import Swal from "sweetalert2";
import { baseURL } from "../../../../Services/api/baseURL";
import CommonModal from "../../../UiKits/Modals/common/modal";

const EMPTY_FORM = {
  eyebrow: "Wholesale liquor portal · Melbourne",
  headline: "Stock your venue\nby 7am tomorrow.",
  headline_highlight: "7am tomorrow.",
  description: "Net trade pricing across 2,400+ SKUs. Next-day delivery to licensed venues in Wyndham & the West. Tax invoice on the spot. No reps, no minimum commit.",
  stats: [
    { value: "2,400+", label: "SKUs in catalogue" },
    { value: "$200", label: "Free delivery threshold" },
    { value: "24h", label: "Standard turnaround" },
  ],
  licence_number: "36117",
  licence_label: "VIC LIQUOR LICENCE",
  licence_note: "Wholesale supply only. Must hold a current Victorian liquor licence.",
  status: "active",
};

const getToken = () => JSON.parse(localStorage.getItem("token"));

export default function LandingContentManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseURL}/api/business/landing-content`, {
        headers: { Authorization: getToken() },
      });
      setItems(res.data?.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); }, []);

  const openAdd = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
    setModal(true);
  };

  const openEdit = (item) => {
    setEditId(item._id);
    setForm({
      eyebrow: item.eyebrow || "",
      headline: item.headline || "",
      headline_highlight: item.headline_highlight || "",
      description: item.description || "",
      stats: item.stats?.length
        ? item.stats.map((s) => ({ value: s.value, label: s.label }))
        : [{ value: "", label: "" }],
      licence_number: item.licence_number || "",
      licence_label: item.licence_label || "",
      licence_note: item.licence_note || "",
      status: item.status || "inactive",
    });
    setModal(true);
  };

  const handleField = (key, val) =>
    setForm((prev) => ({ ...prev, [key]: val }));

  // Stats helpers
  const addStat = () =>
    setForm((prev) => ({ ...prev, stats: [...prev.stats, { value: "", label: "" }] }));

  const removeStat = (i) =>
    setForm((prev) => ({ ...prev, stats: prev.stats.filter((_, idx) => idx !== i) }));

  const updateStat = (i, key, val) =>
    setForm((prev) => {
      const stats = [...prev.stats];
      stats[i] = { ...stats[i], [key]: val };
      return { ...prev, stats };
    });

  const handleSubmit = async () => {
    if (!form.headline?.trim() || !form.description?.trim()) {
      Swal.fire({ title: "Headline and description are required", icon: "warning", confirmButtonColor: "#d3178a" });
      return;
    }
    try {
      if (editId) {
        await axios.patch(
          `${baseURL}/api/business/landing-content/${editId}`,
          form,
          { headers: { Authorization: getToken() } }
        );
      } else {
        await axios.post(
          `${baseURL}/api/business/landing-content`,
          form,
          { headers: { Authorization: getToken() } }
        );
      }
      setModal(false);
      fetch();
      Swal.fire({ title: editId ? "Updated!" : "Created!", icon: "success", confirmButtonColor: "#d3178a" });
    } catch (err) {
      Swal.fire({ title: err?.response?.data?.message || "Failed", icon: "error", confirmButtonColor: "#d3178a" });
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete this content?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Delete",
    });
    if (!result.isConfirmed) return;
    try {
      await axios.delete(`${baseURL}/api/business/landing-content/${id}`, {
        headers: { Authorization: getToken() },
      });
      fetch();
      Swal.fire({ title: "Deleted", icon: "success", confirmButtonColor: "#d3178a" });
    } catch (e) {
      Swal.fire({ title: "Failed", icon: "error", confirmButtonColor: "#d3178a" });
    }
  };

  const handleToggleStatus = async (item) => {
    try {
      await axios.patch(
        `${baseURL}/api/business/landing-content/${item._id}`,
        { status: item.status === "active" ? "inactive" : "active" },
        { headers: { Authorization: getToken() } }
      );
      fetch();
    } catch (e) {
      Swal.fire({ title: "Failed to update status", icon: "error", confirmButtonColor: "#d3178a" });
    }
  };

  return (
    <Fragment>
      <Container fluid={true} style={{ paddingTop: '30px' }}>
        <Row>
          <Col sm="12">
            <Card>
              <CardBody style={{ padding: '15px' }}>

                <Fragment>
                  {/* Header */}
                  <Row className="pb-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h5 className="fw-bolder mb-0">Landing Page Content</h5>
                        <small className="text-muted">Manage the signin page left panel — headline, stats, licence badge</small>
                      </div>
                      <Button
                        onClick={openAdd}
                        style={{ background: "#d3178a", border: "none", borderRadius: "12px", padding: "8px 20px", fontWeight: 600 }}
                      >
                        + Add Content
                      </Button>
                    </div>
                  </Row>

                  {/* List */}
                  {loading ? (
                    <p className="text-center py-5 text-muted">Loading…</p>
                  ) : items.length === 0 ? (
                    <p className="text-center py-5 text-muted">No landing content yet. Click "+ Add Content" to create one.</p>
                  ) : (
                    <div className="d-flex flex-column gap-3">
                      {items.map((item) => (
                        <div
                          key={item._id}
                          style={{
                            background: "#fff",
                            border: "1px solid #E1E6EF",
                            borderRadius: "12px",
                            padding: "16px 20px",
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                            <div className="flex-1">
                              {/* Status badge */}
                              <span
                                style={{
                                  background: item.status === "active" ? "#e8f5e9" : "#f3f4f6",
                                  color: item.status === "active" ? "#16a34a" : "#6b7280",
                                  padding: "2px 10px",
                                  borderRadius: "999px",
                                  fontSize: "0.7rem",
                                  fontWeight: 700,
                                  marginBottom: "6px",
                                  display: "inline-block",
                                }}
                              >
                                {item.status === "active" ? "● Active" : "○ Inactive"}
                              </span>

                              <p className="mb-1 text-xs text-muted" style={{ fontSize: "11px" }}>
                                {item.eyebrow}
                              </p>

                              <h6 className="fw-bold mb-1" style={{ fontSize: "15px", whiteSpace: "pre-line" }}>
                                {item.headline}
                              </h6>
                              <p className="mb-2 text-muted" style={{ fontSize: "12px", maxWidth: "500px" }}>
                                {item.description?.slice(0, 120)}{item.description?.length > 120 ? "…" : ""}
                              </p>

                              {/* Stats preview */}
                              {item.stats?.length > 0 && (
                                <div className="d-flex gap-3 flex-wrap mb-2">
                                  {item.stats.map((s, i) => (
                                    <div key={i} style={{ borderLeft: "2px solid #d3178a", paddingLeft: "8px" }}>
                                      <div style={{ fontWeight: 700, fontSize: "14px" }}>{s.value}</div>
                                      <div style={{ fontSize: "10px", color: "#6b7280" }}>{s.label}</div>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Licence badge */}
                              <span style={{
                                background: "#fff8e1", color: "#C77B0F",
                                fontSize: "10px", fontWeight: 700,
                                padding: "2px 8px", borderRadius: "4px",
                                fontFamily: "monospace",
                              }}>
                                {item.licence_label} {item.licence_number}
                              </span>
                            </div>

                            {/* Actions */}
                            <div className="d-flex gap-2 align-items-center">
                              <button
                                type="button"
                                onClick={() => handleToggleStatus(item)}
                                title={item.status === "active" ? "Deactivate" : "Activate"}
                                style={{
                                  background: item.status === "active" ? "#fce4ec" : "#e8f5e9",
                                  color: item.status === "active" ? "#dc2626" : "#16a34a",
                                  border: "none", borderRadius: "8px",
                                  padding: "6px 12px", fontWeight: 600,
                                  fontSize: "12px", cursor: "pointer",
                                }}
                              >
                                {item.status === "active" ? "Deactivate" : "Activate"}
                              </button>
                              <button
                                type="button"
                                onClick={() => openEdit(item)}
                                style={{
                                  background: "#f3f4f6", border: "none",
                                  borderRadius: "8px", padding: "6px 10px", cursor: "pointer",
                                }}
                              >
                                <Edit2 size={14} />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDelete(item._id)}
                                style={{
                                  background: "#fce4ec", border: "none",
                                  borderRadius: "8px", padding: "6px 10px",
                                  cursor: "pointer", color: "#dc2626",
                                }}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* ── Add / Edit Modal ─────────────────────────────────────────────── */}
                  <CommonModal
                    isOpen={modal}
                    title={editId ? "Edit Landing Content" : "Add Landing Content"}
                    toggler={() => setModal(false)}
                    size="lg"
                  >
                    <Container>
                      <Form>

                        {/* Eyebrow */}
                        <FormGroup>
                          <Label className="font-medium text-sm">
                            Eyebrow text
                            <small className="text-muted ms-2">(small line above headline)</small>
                          </Label>
                          <Input
                            value={form.eyebrow}
                            onChange={(e) => handleField("eyebrow", e.target.value)}
                            placeholder="Wholesale liquor portal · Melbourne"
                          />
                        </FormGroup>

                        {/* Headline */}
                        {/* <FormGroup>
                          <Label className="font-medium text-sm">
                            Headline <span className="text-danger">*</span>
                          </Label>
                          <Input
                            value={form.headline}
                            onChange={(e) => handleField("headline", e.target.value)}
                            placeholder="Stock your venue by 7am tomorrow."
                          />
                        </FormGroup> */}
                        <FormGroup>
                          <Label className="font-medium text-sm">
                            Headline <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="textarea"
                            rows={2}
                            value={form.headline}
                            onChange={(e) => handleField("headline", e.target.value)}
                            placeholder={"Stock your venue\nby 7am tomorrow."}
                          />
                          <small className="text-muted">
                            Press Enter where the line should break on the signin page (e.g. after "venue").
                          </small>
                        </FormGroup>

                        {/* Headline highlight */}
                        {/* <FormGroup>
                          <Label className="font-medium text-sm">
                            Highlighted part of headline
                            <small className="text-muted ms-2">(must match part of headline exactly)</small>
                          </Label>
                          <Input
                            value={form.headline_highlight}
                            onChange={(e) => handleField("headline_highlight", e.target.value)}
                            placeholder="7am tomorrow."
                          />
                          <small className="text-muted">
                            This text will be highlighted with a pink underline on the signin page.
                          </small>
                        </FormGroup> */}
                        {/* Headline highlight */}
                        <FormGroup>
                          <Label className="font-medium text-sm">
                            Highlighted part of headline
                            <small className="text-muted ms-2">(must match part of headline exactly)</small>
                          </Label>
                          <Input
                            value={form.headline_highlight}
                            onChange={(e) => handleField("headline_highlight", e.target.value)}
                            placeholder="7am tomorrow."
                          />
                          <small className="text-muted">
                            This text will be highlighted with a pink underline on the signin page.
                          </small>
                          {form.headline_highlight?.trim() && !form.headline.includes(form.headline_highlight) && (
                            <div
                              style={{
                                marginTop: "6px",
                                background: "#fef3c7",
                                color: "#92400e",
                                fontSize: "12px",
                                padding: "6px 10px",
                                borderRadius: "6px",
                              }}
                            >
                              ⚠️ This text doesn't appear in the Headline above — it won't be highlighted on the signin page.
                            </div>
                          )}
                        </FormGroup>

                        {/* Description */}
                        <FormGroup>
                          <Label className="font-medium text-sm">
                            Description <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="textarea"
                            rows={3}
                            value={form.description}
                            onChange={(e) => handleField("description", e.target.value)}
                            placeholder="Net trade pricing across 2,400+ SKUs…"
                          />
                        </FormGroup>

                        {/* Stats */}
                        <FormGroup>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <Label className="font-medium text-sm mb-0">Stats (bottom row)</Label>
                            <button
                              type="button"
                              onClick={addStat}
                              style={{
                                background: "#fff0f7", color: "#d3178a",
                                border: "1px solid #f9a8d4", borderRadius: "8px",
                                padding: "4px 10px", fontSize: "12px",
                                fontWeight: 600, cursor: "pointer",
                              }}
                            >
                              + Add stat
                            </button>
                          </div>
                          <div className="d-flex flex-column gap-2">
                            {form.stats.map((stat, i) => (
                              <div key={i} className="d-flex gap-2 align-items-center">
                                <Input
                                  value={stat.value}
                                  onChange={(e) => updateStat(i, "value", e.target.value)}
                                  placeholder="2,400+"
                                  style={{ width: "120px", flexShrink: 0 }}
                                />
                                <Input
                                  value={stat.label}
                                  onChange={(e) => updateStat(i, "label", e.target.value)}
                                  placeholder="SKUs in catalogue"
                                />
                                <button
                                  type="button"
                                  onClick={() => removeStat(i)}
                                  style={{
                                    background: "#fce4ec", border: "none",
                                    borderRadius: "6px", padding: "6px 8px",
                                    cursor: "pointer", color: "#dc2626", flexShrink: 0,
                                  }}
                                >
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            ))}
                          </div>
                        </FormGroup>

                        {/* Licence */}
                        <Row>
                          <Col md={4}>
                            <FormGroup>
                              <Label className="font-medium text-sm">Licence label</Label>
                              <Input
                                value={form.licence_label}
                                onChange={(e) => handleField("licence_label", e.target.value)}
                                placeholder="VIC LIQUOR LICENCE"
                              />
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup>
                              <Label className="font-medium text-sm">Licence number</Label>
                              <Input
                                value={form.licence_number}
                                onChange={(e) => handleField("licence_number", e.target.value)}
                                placeholder="36117"
                              />
                            </FormGroup>
                          </Col>
                          <Col md={5}>
                            <FormGroup>
                              <Label className="font-medium text-sm">Licence note</Label>
                              <Input
                                value={form.licence_note}
                                onChange={(e) => handleField("licence_note", e.target.value)}
                                placeholder="Wholesale supply only…"
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        {/* Status */}
                        <FormGroup>
                          <Label className="font-medium text-sm">Status</Label>
                          <div className="d-flex gap-4">
                            <label className="d-flex align-items-center gap-1">
                              <input
                                type="radio"
                                checked={form.status === "active"}
                                onChange={() => handleField("status", "active")}
                                className="accent-[#d3178a]"
                              />
                              Active
                            </label>
                            <label className="d-flex align-items-center gap-1">
                              <input
                                type="radio"
                                checked={form.status === "inactive"}
                                onChange={() => handleField("status", "inactive")}
                              />
                              Inactive
                            </label>
                          </div>
                          <small className="text-muted">
                            Setting to active will deactivate the currently active content.
                          </small>
                        </FormGroup>

                        <div className="text-end mt-3">
                          <Button
                            type="button"
                            onClick={handleSubmit}
                            style={{
                              background: "#d3178a", border: "none",
                              borderRadius: "12px", padding: "8px 28px",
                              fontWeight: 600,
                            }}
                          >
                            {editId ? "Update" : "Save"}
                          </Button>
                        </div>

                      </Form>
                    </Container>
                  </CommonModal>
                </Fragment>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
}